import { apiClient, type ManualCommandConfig } from '@/services/http/client'
import { id, object, digest, scopedActions, type Data } from '@/services/legalControl/workflow'
export const businessPath = '/clients/business-verifications'
export interface IntakeReference {
  request: string
  unknown: boolean
  cancel?: string
}
export interface BusinessSource extends Data {
  customer_id: number
  alias_id: string
  client_id: string
  source_epoch: string
  source_revision: number
  source_sha256: string
}
const integer = (v: unknown): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= 2147483647
const key = (firm: string, user: string) => `taxhive.business-intake.v1:${id(firm)}:${id(user)}`
const bodyKey = (firm: string, user: string, session: string, request: string) =>
  `${key(firm, user)}:${id(session)}:${id(request)}`
export function intakeHistory(firm: string, user: string): IntakeReference[] {
  const raw = localStorage.getItem(key(firm, user))
  if (!raw) return []
  const rows: unknown = JSON.parse(raw)
  if (!Array.isArray(rows) || rows.length > 100)
    throw new Error('Original business references unavailable')
  const seen = new Set<string>()
  return rows.map(v => {
    const row = object(v)
    if (
      Object.keys(row).some(k => !['request', 'unknown', 'cancel'].includes(k)) ||
      typeof row.unknown !== 'boolean' ||
      seen.has(id(row.request))
    )
      throw new Error('Original business history unavailable')
    seen.add(id(row.request))
    return {
      request: id(row.request),
      unknown: row.unknown,
      ...(row.cancel !== undefined ? { cancel: id(row.cancel) } : {}),
    }
  })
}
export function rememberIntake(firm: string, user: string, ref: IntakeReference) {
  id(ref.request)
  if (ref.cancel) id(ref.cancel)
  const rows = intakeHistory(firm, user),
    prior = rows.find(r => r.request === ref.request)
  if (prior?.cancel && prior.cancel !== ref.cancel)
    throw new Error('Keep original cancellation reference')
  const rest = rows.filter(r => r.request !== ref.request),
    next = [ref, ...rest.filter(r => r.unknown), ...rest.filter(r => !r.unknown).slice(0, 19)]
  if (next.length > 100) throw new Error('Recover original business requests before writing')
  const raw = JSON.stringify(next)
  localStorage.setItem(key(firm, user), raw)
  if (localStorage.getItem(key(firm, user)) !== raw)
    throw new Error('Business references were not saved')
}
export function intakeCommand(value: unknown): Data {
  const c = object(value),
    keys = [
      'request_id',
      'customer_id',
      'alias_id',
      'client_id',
      'source_epoch',
      'source_revision',
      'source_sha256',
      'proposed_account_id',
      'declared_capacity',
      'requested_role',
      'requested_actions',
      'declared_evidence',
      'acknowledge_unverified_request',
    ]
  if (
    Object.keys(c).sort().join(',') !== keys.sort().join(',') ||
    !integer(c.customer_id) ||
    !integer(c.source_revision) ||
    typeof c.source_sha256 !== 'string' ||
    !digest.test(c.source_sha256) ||
    typeof c.declared_capacity !== 'string' ||
    !/^[A-Za-z][A-Za-z0-9._-]{0,79}$/.test(c.declared_capacity) ||
    (c.requested_role !== 'legal_controller' && c.requested_role !== 'representative') ||
    c.acknowledge_unverified_request !== true
  )
    throw new Error('Exact original business declaration required')
  for (const k of ['request_id', 'alias_id', 'client_id', 'source_epoch', 'proposed_account_id'])
    if (id(c[k]) !== c[k]) throw new Error('Exact canonical references required')
  scopedActions(c.requested_actions)
  if (
    !Array.isArray(c.declared_evidence) ||
    !c.declared_evidence.length ||
    c.declared_evidence.length > 10
  )
    throw new Error('Select declared evidence')
  const seen = new Set<string>()
  for (const v of c.declared_evidence) {
    const e = object(v)
    if (
      Object.keys(e).sort().join(',') !== 'digest,evidence_id' ||
      seen.has(id(e.evidence_id)) ||
      typeof e.digest !== 'string' ||
      !digest.test(e.digest)
    )
      throw new Error('Exact distinct evidence required')
    seen.add(id(e.evidence_id))
  }
  return c
}
export function storeIntakeBody(firm: string, user: string, session: string, value: Data) {
  const c = intakeCommand(value),
    k = bodyKey(firm, user, session, id(c.request_id)),
    raw = JSON.stringify(c),
    previous = sessionStorage.getItem(k)
  if (previous && JSON.stringify(intakeCommand(JSON.parse(previous))) !== raw)
    throw new Error('Original declaration differs')
  sessionStorage.setItem(k, raw)
  if (sessionStorage.getItem(k) !== raw) throw new Error('Original declaration was not saved')
}
export function originalIntake(
  firm: string,
  user: string,
  session: string,
  request: string
): Data | null {
  const raw = sessionStorage.getItem(bodyKey(firm, user, session, request))
  if (!raw) return null
  const c = intakeCommand(JSON.parse(raw))
  if (c.request_id !== request) throw new Error('Original declaration differs')
  return c
}
export function intakeReceipt(value: unknown, request: string): Data {
  const r = object(value)
  if (
    r.request_id !== id(request) ||
    typeof r.state !== 'string' ||
    !['pending', 'source_held', 'dispatch_pending', 'cancelled', 'no_effect'].includes(r.state) ||
    r.source_held !== ['source_held', 'dispatch_pending'].includes(r.state) ||
    r.authority !== 'unverified_business_intake_only' ||
    r.authority_granted !== false ||
    r.controller_admitted !== false
  )
    throw new Error('Original unverified business receipt unavailable')
  return r
}
export async function businessSource(customer: number, firm: string): Promise<BusinessSource> {
  if (!integer(customer)) throw new Error('Select a business customer')
  const r = object((await apiClient.get(`${businessPath}/sources/${customer}`)).data)
  if (
    r.schema_version !== 1 ||
    r.source_owner !== 'customer' ||
    r.source_kind !== 'firm_business_customer' ||
    r.subject_type !== 'business' ||
    r.firm_id !== id(firm) ||
    r.customer_id !== customer ||
    !integer(r.source_revision) ||
    typeof r.source_sha256 !== 'string' ||
    !digest.test(r.source_sha256) ||
    r.authority !== 'versioned_business_observation_only' ||
    [r.legal_subject_verified, r.evidence_verified, r.authority_granted, r.reservation_held].some(
      v => v !== false
    ) ||
    typeof r.observed_at !== 'string' ||
    !Number.isFinite(Date.parse(r.observed_at))
  )
    throw new Error('Current original business source unavailable')
  for (const k of ['alias_id', 'client_id', 'source_epoch']) id(r[k])
  return r as BusinessSource
}
export async function businessChoices(
  page: number
): Promise<{ items: Array<{ customer_id: number; business_name: string }>; total: number }> {
  if (!integer(page)) throw new Error('Choose a valid page')
  const r = object(
    (await apiClient.get('/business-customers', { params: { page, page_size: 20 } })).data
  )
  if (
    r.page !== page ||
    r.page_size !== 20 ||
    typeof r.total !== 'number' ||
    !Number.isSafeInteger(r.total) ||
    r.total < 0 ||
    !Array.isArray(r.items) ||
    r.items.length !== Math.min(20, Math.max(0, r.total - (page - 1) * 20))
  )
    throw new Error('Business customer list unavailable')
  const seen = new Set<number>()
  const items = r.items.map(v => {
    const row = object(v)
    if (
      !integer(row.customer_id) ||
      typeof row.business_name !== 'string' ||
      !row.business_name.length ||
      row.business_name.length > 255 ||
      seen.has(row.customer_id)
    )
      throw new Error('Business customer list unavailable')
    seen.add(row.customer_id)
    return { customer_id: row.customer_id, business_name: row.business_name }
  })
  return { items, total: r.total }
}
export async function inspectIntake(request: string) {
  return intakeReceipt((await apiClient.get(`${businessPath}/${id(request)}`)).data, request)
}
export async function submitIntake(command: Data) {
  const c = intakeCommand(command)
  return intakeReceipt(
    (await apiClient.post(businessPath, c, { manualCommand: true } as ManualCommandConfig)).data,
    id(c.request_id)
  )
}
export async function cancelIntake(command: Data, cancel: string, reason: string) {
  const original = intakeCommand(command)
  if (!['withdrawn', 'incorrect_request', 'evidence_correction'].includes(reason))
    throw new Error('Choose a cancellation reason')
  return intakeReceipt(
    (
      await apiClient.post(
        `${businessPath}/${id(original.request_id)}/cancel`,
        { original, command_id: id(cancel), reason },
        { manualCommand: true } as ManualCommandConfig
      )
    ).data,
    id(original.request_id)
  )
}
export function storedCancellation(
  firm: string,
  user: string,
  session: string,
  request: string,
  cancel: string,
  reason?: string
): string {
  const k = bodyKey(firm, user, session, request) + ':cancel:' + id(cancel),
    previous = sessionStorage.getItem(k)
  if (previous) {
    const row = object(JSON.parse(previous))
    if (
      Object.keys(row).join(',') !== 'reason' ||
      typeof row.reason !== 'string' ||
      !['withdrawn', 'incorrect_request', 'evidence_correction'].includes(row.reason)
    )
      throw new Error('Original cancellation unavailable')
    return row.reason
  }
  if (!reason || !['withdrawn', 'incorrect_request', 'evidence_correction'].includes(reason))
    throw new Error('Original cancellation unavailable')
  const raw = JSON.stringify({ reason })
  sessionStorage.setItem(k, raw)
  if (sessionStorage.getItem(k) !== raw) throw new Error('Original cancellation was not saved')
  return reason
}
